import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { logError } from "@/lib/logger";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/lib/features";
import { Link } from "react-router-dom";
import ScreeningPanel from "@/components/ScreeningPanel";
import SimilarCandidates from "@/components/SimilarCandidates";
import ScheduleInterviewModal from "@/components/ScheduleInterviewModal";
import InterviewAttendanceToggle from "@/components/InterviewAttendanceToggle";
import InterviewAssessmentPanel from "@/components/InterviewAssessmentPanel";
import AttendanceNudgeModal from "@/components/AttendanceNudgeModal";
import EmailCandidateModal from "@/components/EmailCandidateModal";
import WhatsAppCandidateModal from "@/components/WhatsAppCandidateModal";
import OfferHireOutcomeModal from "@/components/OfferHireOutcomeModal";
import InterviewOutcomeModal from "@/components/InterviewOutcomeModal";
import SendBookingLinkModal from "@/components/SendBookingLinkModal";
import EditCandidateModal from "@/components/EditCandidateModal";
import ReferencesSection from "@/components/ReferencesSection";
import ResumeActions from "@/components/ResumeActions";
import CvActions from "@/components/CvActions";
import CvFreshnessBadge from "@/components/CvFreshnessBadge";
import VerifiedTierChip from "@/components/VerifiedTierChip";
import { stageEnteredLabel } from "@/lib/stageHistory";
import {
    X,
    Sparkle,
    Lock,
    User,
    EnvelopeSimple,
    LinkSimple,
    Copy,
    Phone,
    MapPin,
    Briefcase,
    LinkedinLogo,
    Star,
    CalendarPlus,
    VideoCamera,
    ArrowSquareOut,
    PaperPlaneTilt,
    WhatsappLogo,
    PencilSimple,
} from "@phosphor-icons/react";

const STAGES = ["Applied", "Screening", "Interview", "Offer", "Hired", "Rejected"];

// V1.5.3 — drawer header avatar. Falls back to initials on missing/broken.
function initialsOf(name) {
    return String(name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}
function DrawerAvatar({ url, name }) {
    const [broken, setBroken] = React.useState(false);
    return (
        <div
            data-testid="drawer-avatar"
            className="shrink-0 w-16 h-16 bg-cobalt text-white font-black flex items-center justify-center overflow-hidden text-lg rounded"
        >
            {url && !broken ? (
                <img src={url} alt="" onError={() => setBroken(true)}
                     className="w-full h-full object-cover" />
            ) : (
                <span>{initialsOf(name)}</span>
            )}
        </div>
    );
}

function MatchBadge({ score }) {
    if (score == null) return null;
    let cls = "bg-[#FEF2F2] text-[#DC2626]";
    let label = "Weak";
    if (score >= 75) {
        cls = "bg-[#ECFDF5] text-[#059669]";
        label = "Strong";
    } else if (score >= 55) {
        cls = "bg-[#EFF6FF] text-[#F59E0B]";
        label = "Good";
    } else if (score >= 35) {
        cls = "bg-[#F5F5F5] text-[#525252]";
        label = "Average";
    }
    return (
        <span
            data-testid="match-badge"
            className={`px-2 py-1 text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1 ${cls}`}
        >
            <Sparkle size={10} weight="fill" /> {score}% · {label}
        </span>
    );
}

export default function CandidateDrawer({ candidateId, onClose, onUpdated, onOpenCandidate }) {
    const { user: me } = useAuth();
    const { isEnabled } = useFeatures();
    const canWrite = ["admin", "recruiter", "hiring_manager", "superadmin"].includes(me?.role);
    const aiMatchEnabled = isEnabled("ai_matching");
    const [c, setC] = useState(null);
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(false);
    const [computing, setComputing] = useState(false);
    const [note, setNote] = useState("");
    const [rating, setRating] = useState(3);
    const [err, setErr] = useState("");
    const [showSchedule, setShowSchedule] = useState(false);
    // V1.5.7.1 — Attendance nudge modal state. Fires once per drawer open
    // when the candidate has ≥1 past interview without a marked attendance.
    const [nudgeShown, setNudgeShown] = useState(false);
    const [nudgeOverdue, setNudgeOverdue] = useState([]);
    const [showEmail, setShowEmail] = useState(false);
    const [showWhatsApp, setShowWhatsApp] = useState(false);
    // Sub-status outcome capture for Offer / Hired stages. Auto-opens when a
    // recruiter moves the candidate into either stage (mirrors the flow that
    // already exists on the 360° Person Detail panel).
    const [outcomeModal, setOutcomeModal] = useState(null); // null | "offer" | "hire" | "interview"
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [requestingTestimonial, setRequestingTestimonial] = useState(false);
    const [sendingLoginLink, setSendingLoginLink] = useState(false);
    const [copyingLink, setCopyingLink] = useState(false);

    const sendLoginLink = useCallback(async () => {
        if (!c?.id) return;
        setSendingLoginLink(true);
        try {
            const { data } = await api.post(`/candidates/${c.id}/send-login-link`);
            toast.success(data?.message || `Sign-in link sent to ${c.email}.`);
        } catch (ex) {
            toast.error(formatApiError(ex) || "Could not send the login link.");
        } finally {
            setSendingLoginLink(false);
        }
    }, [c?.id, c?.email]);

    // Feb 11, 2026 — recruiters need a fallback when transactional email is
    // delayed (Gmail/Yahoo can hold Resend for minutes). This mints/reuses
    // the same magic token via `send_email=false` and copies the URL to
    // clipboard so it can be shared over WhatsApp / SMS / phone.
    const copyLoginLink = useCallback(async () => {
        if (!c?.id) return;
        setCopyingLink(true);
        try {
            const { data } = await api.post(
                `/candidates/${c.id}/send-login-link?send_email=false`,
            );
            const link = data?.magic_url;
            if (!link) {
                toast.error("No link was returned. Try again.");
                return;
            }
            // Prefer async Clipboard API; fall back to a hidden textarea for
            // older browsers / non-secure contexts.
            let copied = false;
            try {
                await navigator.clipboard.writeText(link);
                copied = true;
            } catch {
                const ta = document.createElement("textarea");
                ta.value = link;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                try { copied = document.execCommand("copy"); } catch { /* noop */ }
                document.body.removeChild(ta);
            }
            if (copied) {
                toast.success(
                    "Sign-in link copied — paste it in WhatsApp / SMS.",
                    { duration: 4000 },
                );
            } else {
                toast.info(link, { duration: 12000 });
            }
        } catch (ex) {
            toast.error(formatApiError(ex) || "Could not generate the login link.");
        } finally {
            setCopyingLink(false);
        }
    }, [c?.id]);

    const requestTestimonial = async () => {
        setRequestingTestimonial(true);
        try {
            const { data } = await api.post(
                `/candidates/${candidateId}/request-testimonial`,
                { channel: c?.phone ? "whatsapp" : "email" },
            );
            // Open the appropriate channel with the pre-composed message.
            // Use window.location.assign — that navigates the current tab,
            // works with mailto:/wa.me schemes even after an await gesture,
            // and isn't caught by popup blockers or iframe sandboxes.
            const url = data.whatsapp_url || data.mailto_url;
            if (url) window.location.assign(url);
            // Reflect the "Requested" state on the drawer
            setC((prev) => prev ? { ...prev, testimonial_requested_at: data.requested_at } : prev);
        } catch (e) {
            const { toast } = await import("sonner");
            toast.error(e.response?.data?.detail || "Couldn't prepare the nudge.");
        } finally {
            setRequestingTestimonial(false);
        }
    };

    const fetchCandidate = useCallback(() => {
        if (!candidateId) return;
        setLoading(true);
        api
            .get(`/candidates/${candidateId}`)
            .then(async (r) => {
                setC(r.data);
                if (r.data?.job_id) {
                    try {
                        const j = await api.get(`/jobs/${r.data.job_id}`);
                        setJob(j.data);
                    } catch (e) {
                        logError("Failed to load job for screening:", e);
                    }
                }
            })
            .catch((e) => logError("Failed to load candidate:", e))
            .finally(() => setLoading(false));
    }, [candidateId]);

    useEffect(() => {
        // V1.5.8.16 — clear stale candidate state when the drawer target
        // changes so we never render a mix of two candidates (previous CV
        // chip + new resume_text arriving mid-frame). Manual re-fetches
        // (Re-parse, edits) go through fetchCandidate() directly and keep
        // the visible data intact.
        setC(null);
        setJob(null);
        setNudgeShown(false);
        setNudgeOverdue([]);
        fetchCandidate();
    }, [fetchCandidate]);

    // V1.5.7.1 — Detect overdue interviews after candidate loads and pop the
    // attendance nudge modal ONCE per drawer session. `nudgeShown` guards
    // against re-firing on every re-fetch.
    useEffect(() => {
        if (!c || nudgeShown) return;
        const NUDGE_MIN_MS = 30 * 60 * 1000;
        const now = Date.now();
        const overdue = (c.interviews || [])
            .map((iv) => ({ ...iv, id: iv.id || iv.event_id }))
            .filter((iv) => {
                if (iv.attended === true || iv.attended === false) return false;
                const endMs = iv.end ? new Date(iv.end).getTime() : null;
                return endMs !== null && now > (endMs + NUDGE_MIN_MS);
            });
        if (overdue.length > 0) {
            setNudgeOverdue(overdue);
            setNudgeShown(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [c?.id]);

    const updateStage = async (stage) => {
        // Feb 2026 — Mandatory-registration gate. Surface a friendly warning
        // instead of letting the 409 bubble as an unhandled promise rejection.
        if (
            c &&
            !c.candidate_user_id &&
            c.drip_state?.status === "active" &&
            !["Applied", "Rejected"].includes(stage)
        ) {
            toast.info(`Candidate hasn't registered yet — send a reminder before moving to ${stage}.`, { duration: 5000 });
            return;
        }
        try {
            const r = await api.patch(`/candidates/${candidateId}/stage`, { stage });
            setC(r.data);
            onUpdated?.();
            // Feb 2026 — auto-open the sub-status outcome modal after landing
            // on Offer / Hired. Only surface it when no outcome has been
            // captured yet, so a normal re-drag doesn't re-nag the recruiter.
            if (stage === "Offer" && !r.data?.offer_outcome) {
                setOutcomeModal("offer");
            } else if (stage === "Hired" && !r.data?.hire_outcome) {
                setOutcomeModal("hire");
            }
            // Interview stage does NOT auto-open — the interview happens
            // days later. Recruiter manually clicks "Record interview outcome"
            // after the actual interview.
        } catch (e) {
            // Feb 18, 2026 — the intake-incomplete gate (409) returns
            // `detail={code: "INTAKE_INCOMPLETE", message, missing}` — an
            // OBJECT, not a string. Handing it straight to toast.error()
            // previously crashed the whole React tree (error #31). Prefer
            // the structured message when available and fall back to
            // `formatApiError` which stringifies safely.
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "INTAKE_INCOMPLETE") {
                const missing = Array.isArray(detail.missing) ? detail.missing : [];
                toast.error(
                    detail.message || "Intake form is not fully filled.",
                    missing.length ? { description: `Missing: ${missing.slice(0, 5).join(", ")}${missing.length > 5 ? "…" : ""}` } : undefined,
                );
            } else {
                toast.error(formatApiError(detail) || "Couldn't update stage.");
            }
        }
    };

    const runMatch = async () => {
        setErr("");
        setComputing(true);
        try {
            await api.post(`/candidates/${candidateId}/match`);
            const r = await api.get(`/candidates/${candidateId}`);
            setC(r.data);
            onUpdated?.();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setComputing(false);
        }
    };

    const addNote = async () => {
        if (!note.trim()) return;
        await api.post(`/candidates/${candidateId}/notes`, { text: note, rating });
        const r = await api.get(`/candidates/${candidateId}`);
        setC(r.data);
        setNote("");
    };

    if (!candidateId) return null;

    return (
        <div className="fixed inset-0 z-40" data-testid="candidate-drawer">
            {nudgeOverdue.length > 0 && (
                <AttendanceNudgeModal
                    candidateId={c?.id}
                    overdue={nudgeOverdue}
                    onClose={() => setNudgeOverdue([])}
                    onUpdated={() => { fetchCandidate(); setNudgeOverdue([]); }}
                />
            )}
            <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose} />
            <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[640px] bg-white border-l border-[#E5E5E5] flex flex-col shadow-2xl">
                <div className="p-6 border-b border-[#E5E5E5] flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 min-w-0 flex-1">
                        {/* Avatar (V1.5.3 — a2) */}
                        {c && (
                            <DrawerAvatar url={c.avatar_url} name={c.name} />
                        )}
                        <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-1 flex items-center gap-2">
                                <span>Candidate profile</span>
                                {c?.code && (
                                    <span
                                        data-testid="drawer-candidate-code"
                                        title="Candidate code — unique per workspace"
                                        className="bg-ink text-white px-2 py-0.5 font-mono text-[10px] tracking-wider normal-case"
                                    >
                                        {c.code}
                                    </span>
                                )}
                            </p>
                            <h2 className="font-heading text-3xl font-black tracking-tighter flex items-center gap-2 flex-wrap">
                                <span>{c?.name || "—"}</span>
                                {/* Feb 18, 2026 — verification chip is the primary
                                    signal recruiters use to trust a candidate;
                                    surfacing it prominently next to the name
                                    (previously only rendered on the tiny kanban
                                    card, easy to miss). */}
                                {c && <VerifiedTierChip tier={c.verification_tier} />}
                            </h2>
                            <p className="text-sm text-[#525252] mt-1">{c?.current_title || "—"}</p>
                            {c && (
                                <div className="mt-2 flex items-center gap-2 flex-wrap">
                                    <CvFreshnessBadge candidate={c} />
                                    <CvActions candidate={c} compact />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {canWrite && c && (() => {
                            // Recruiters may edit only their own candidates (or
                            // team-pool candidates). Admins/HMs/superadmins
                            // may edit any candidate in scope. Backend enforces
                            // this too — the UI hides the button for clarity.
                            const canEdit =
                                me?.role !== "recruiter"
                                || c.owner_id === me?.id
                                || !!c.is_team_pool;
                            if (!canEdit) return null;
                            return (
                                <button
                                    type="button"
                                    data-testid="drawer-edit-candidate-btn"
                                    onClick={() => setShowEditModal(true)}
                                    title="Edit candidate details"
                                    className="p-2 hover:bg-[#F5F5F5] text-[#525252] hover:text-ink transition-colors"
                                >
                                    <PencilSimple size={16} weight="bold" />
                                </button>
                            );
                        })()}
                        <button
                            onClick={onClose}
                            data-testid="drawer-close-button"
                            className="p-2 hover:bg-[#F5F5F5]"
                        >
                            <X size={18} weight="bold" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {loading || !c ? (
                        <p className="p-6 text-[#525252]">Loading…</p>
                    ) : (
                        <div className="p-6 space-y-6">
                            {/* V2.0.74 — Profile-only banner when this
                                candidate belongs to another recruiter and
                                the workspace has full-visibility ON. */}
                            {c._pipeline_redacted && (
                                <div
                                    data-testid="drawer-profile-only-banner"
                                    className="border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs leading-relaxed"
                                >
                                    <span className="font-black uppercase tracking-wider mr-1">Profile only</span>
                                    &middot;{" "}
                                    {c.added_by_name
                                        ? <>This candidate is owned by <strong>{c.added_by_name}</strong>. Pipeline data (stage, notes, activity) stays in their Kanban.</>
                                        : <>Pipeline data (stage, notes, activity) stays private to the owner&apos;s Kanban.</>
                                    }
                                </div>
                            )}
                            {/* Quick facts */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                <div className="flex items-center gap-2 text-[#525252] min-w-0">
                                    <EnvelopeSimple size={14} weight="bold" className="shrink-0" />
                                    <span className="truncate" title={c.email}>{c.email}</span>
                                </div>
                                {c.phone && (
                                    <div className="flex items-center gap-2 text-[#525252] min-w-0">
                                        <Phone size={14} weight="bold" className="shrink-0" />
                                        <span className="truncate" title={c.phone}>{c.phone}</span>
                                    </div>
                                )}
                                {c.location && (
                                    <div className="flex items-center gap-2 text-[#525252] min-w-0">
                                        <MapPin size={14} weight="bold" className="shrink-0" />
                                        <span className="truncate" title={c.location}>{c.location}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 text-[#525252] min-w-0">
                                    <Briefcase size={14} weight="bold" className="shrink-0" />
                                    <span className="truncate">{c.years_experience} yrs</span>
                                </div>
                                {c.linkedin && (
                                    <a
                                        href={c.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-cobalt hover:underline col-span-2"
                                    >
                                        <LinkedinLogo size={14} weight="bold" /> LinkedIn profile
                                    </a>
                                )}
                            </div>

                            {/* Publish to Marketplace toggle — visible only for agency-uploaded candidates */}
                            {canWrite && c.origin === "agency_uploaded" && (
                                <PublishToMarketplaceToggle
                                    candidate={c}
                                    onChanged={async () => {
                                        const r = await api.get(`/candidates/${candidateId}`);
                                        setC(r.data);
                                        onUpdated?.();
                                    }}
                                />
                            )}

                            {/* Quick actions */}
                            {canWrite && (
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        data-testid="open-email-candidate"
                                        onClick={() => setShowEmail(true)}
                                        className="bg-ink hover:bg-cobalt text-white px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors"
                                    >
                                        <PaperPlaneTilt size={12} weight="bold" /> Email candidate
                                    </button>
                                    {c.email && (
                                        <>
                                            <button
                                                data-testid="drawer-send-login-link-btn"
                                                onClick={sendLoginLink}
                                                disabled={sendingLoginLink || copyingLink}
                                                title={`Email a passwordless sign-in link to ${c.email}. Valid for 30 days.`}
                                                className="border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#F5F5F5] disabled:opacity-60 disabled:cursor-not-allowed px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors"
                                            >
                                                <EnvelopeSimple size={12} weight="bold" />
                                                {sendingLoginLink ? "Sending…" : "Send login link"}
                                            </button>
                                            <button
                                                data-testid="drawer-copy-login-link-btn"
                                                onClick={copyLoginLink}
                                                disabled={sendingLoginLink || copyingLink}
                                                title="Copy the sign-in link so you can share it via WhatsApp / SMS. Handy when email is delayed."
                                                className="border border-cobalt text-cobalt hover:bg-cobalt hover:text-white disabled:opacity-60 disabled:cursor-not-allowed px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors"
                                            >
                                                {copyingLink ? (
                                                    <>
                                                        <LinkSimple size={12} weight="bold" />
                                                        Copying…
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy size={12} weight="bold" />
                                                        Copy link
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    )}
                                    {c.phone && (
                                        isEnabled("whatsapp") ? (
                                            <button
                                                data-testid="open-whatsapp-candidate"
                                                onClick={() => setShowWhatsApp(true)}
                                                className="bg-[#25D366] hover:bg-[#1EBE5C] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors"
                                            >
                                                <WhatsappLogo size={12} weight="fill" /> WhatsApp
                                            </button>
                                        ) : (
                                            <Link
                                                to="/billing"
                                                data-testid="open-whatsapp-locked"
                                                title="WhatsApp campaigns are available on Pro and above"
                                                className="border border-[#E5E5E5] text-[#A3A3A3] hover:border-splash hover:text-ink px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors"
                                            >
                                                <Lock size={11} weight="fill" /> WhatsApp
                                            </Link>
                                        )
                                    )}
                                    {c.stage === "Hired" && (
                                        <button
                                            data-testid="request-testimonial-btn"
                                            onClick={requestTestimonial}
                                            disabled={requestingTestimonial}
                                            className="bg-gradient-to-r from-splash to-cobalt hover:opacity-90 text-ink px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-opacity disabled:opacity-50"
                                        >
                                            <Star size={12} weight="fill" />
                                            {c.testimonial_requested_at ? "Nudge again" : "Request testimonial"}
                                        </button>
                                    )}
                                    {c.stage === "Interview" && (
                                        <button
                                            data-testid="drawer-interview-outcome-btn"
                                            onClick={() => setOutcomeModal("interview")}
                                            className="border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#F5F5F5] px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors"
                                        >
                                            {c.interview_outcome
                                                ? `Update interview outcome (${c.interview_outcome.status})`
                                                : "Record interview outcome"}
                                        </button>
                                    )}
                                    {c.stage === "Interview" && (
                                        <button
                                            data-testid="drawer-send-booking-link-btn"
                                            onClick={() => setShowBookingModal(true)}
                                            className="border border-cyan-500 text-cyan-700 bg-cyan-50 hover:bg-cyan-100 px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors"
                                        >
                                            Send booking link
                                        </button>
                                    )}
                                    {c.stage === "Offer" && (
                                        <button
                                            data-testid="drawer-offer-outcome-btn"
                                            onClick={() => setOutcomeModal("offer")}
                                            className="border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#F5F5F5] px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors"
                                        >
                                            {c.offer_outcome ? "Update offer outcome" : "Record offer outcome"}
                                        </button>
                                    )}
                                    {c.stage === "Hired" && (
                                        <button
                                            data-testid="drawer-hire-outcome-btn"
                                            onClick={() => setOutcomeModal("hire")}
                                            className="border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#F5F5F5] px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors"
                                        >
                                            {c.hire_outcome ? "Update joining outcome" : "Confirm joining"}
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Stage selector */}
                            {canWrite && (
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                                        Stage
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {STAGES.map((s) => (
                                            <button
                                                key={s}
                                                data-testid={`set-stage-${s.toLowerCase()}`}
                                                onClick={() => updateStage(s)}
                                                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                                                    c.stage === s
                                                        ? "bg-ink text-white"
                                                        : "border border-[#E5E5E5] text-[#525252] hover:border-ink"
                                                }`}
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                    {stageEnteredLabel(c.stage_history, c.stage) && (
                                        <p
                                            data-testid="current-stage-entered-at"
                                            className="text-[10px] text-[#A3A3A3] mt-2"
                                        >
                                            {stageEnteredLabel(c.stage_history, c.stage)}
                                        </p>
                                    )}
                                </div>
                            )}
                            {!canWrite && (
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                                        Stage
                                    </p>
                                    <span className="inline-block px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-ink text-white">
                                        {c.stage}
                                    </span>
                                    {stageEnteredLabel(c.stage_history, c.stage) && (
                                        <p className="text-[10px] text-[#A3A3A3] mt-2">
                                            {stageEnteredLabel(c.stage_history, c.stage)}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* AI match */}
                            <div className="border border-[#E5E5E5] p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
                                        AI Match Analysis
                                    </p>
                                    {aiMatchEnabled ? (
                                        <button
                                            data-testid="run-match-button"
                                            onClick={runMatch}
                                            disabled={computing || !canWrite}
                                            className="bg-ink hover:bg-cobalt disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 text-xs font-bold tracking-wider uppercase flex items-center gap-2 transition-colors"
                                        >
                                            <Sparkle size={12} weight="fill" />
                                            {computing ? "Analyzing…" : c.match_score != null ? "Re-run" : "Run match"}
                                        </button>
                                    ) : (
                                        <Link
                                            to="/billing"
                                            data-testid="run-match-upgrade"
                                            title="AI Match is available on Pro and Enterprise plans"
                                            className="bg-splash/20 text-ink px-4 py-2 text-xs font-bold tracking-wider uppercase flex items-center gap-2 hover:bg-splash/40 transition-colors"
                                        >
                                            <Lock size={12} weight="fill" />
                                            Upgrade to unlock
                                        </Link>
                                    )}
                                </div>
                                {err && (
                                    <div className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-3 py-2 mb-3">
                                        {err}
                                    </div>
                                )}
                                {c.match_score != null ? (
                                    <div className="space-y-3">
                                        <div className="flex items-baseline gap-3" data-testid="drawer-match-result">
                                            <p className="font-heading font-black text-6xl tracking-tighter text-ink" data-testid="drawer-match-score">
                                                {c.match_score}
                                            </p>
                                            <p className="text-[#525252]" data-testid="drawer-match-verdict">/100 · {c.match_verdict}</p>
                                        </div>
                                        <p className="text-sm text-ink">{c.match_summary}</p>
                                        {c.match_strengths?.length > 0 && (
                                            <div>
                                                <p className="text-xs font-bold uppercase tracking-wider text-[#059669] mb-1">
                                                    Strengths
                                                </p>
                                                <ul className="text-sm text-[#525252] list-disc list-inside space-y-0.5">
                                                    {c.match_strengths.map((s, i) => (
                                                        <li key={`str-${i}-${s.slice(0, 20)}`}>{s}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {c.match_gaps?.length > 0 && (
                                            <div>
                                                <p className="text-xs font-bold uppercase tracking-wider text-[#DC2626] mb-1">
                                                    Gaps
                                                </p>
                                                <ul className="text-sm text-[#525252] list-disc list-inside space-y-0.5">
                                                    {c.match_gaps.map((s, i) => (
                                                        <li key={`gap-${i}-${s.slice(0, 20)}`}>{s}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-sm text-[#525252]">
                                        {aiMatchEnabled
                                            ? "No analysis yet. Run AI match to score this candidate against the JD."
                                            : "AI Match scores candidates against the JD in seconds. Upgrade your plan to unlock this."}
                                    </p>
                                )}
                            </div>

                            {/* Screening Panel — shown for Screening stage or if already screened */}
                            {canWrite &&
                                job &&
                                (c.stage === "Screening" || c.screening_avg != null) && (
                                    <ScreeningPanel
                                        candidate={c}
                                        job={job}
                                        onJobChanged={(j) => setJob(j)}
                                        onCandidateChanged={() => {
                                            fetchCandidate();
                                            onUpdated?.();
                                        }}
                                    />
                                )}

                            {/* Interview scheduling */}
                            {canWrite && (
                                <div className="border border-[#E5E5E5] p-5" data-testid="interview-panel">
                                    <div className="flex items-center justify-between mb-3">
                                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
                                            Interview
                                        </p>
                                        <button
                                            data-testid="open-schedule-interview"
                                            onClick={() => setShowSchedule(true)}
                                            disabled={c.stage !== "Interview"}
                                            title={c.stage !== "Interview" ? "Move candidate to Interview stage first" : ""}
                                            className="bg-ink hover:bg-cobalt disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 text-xs font-bold tracking-wider uppercase flex items-center gap-2 transition-colors"
                                        >
                                            <CalendarPlus size={12} weight="bold" />
                                            Schedule interview
                                        </button>
                                    </div>
                                    {c.stage !== "Interview" && (!c.interviews || c.interviews.length === 0) && (
                                        <p className="text-sm text-[#A3A3A3]">
                                            Move this candidate to the <span className="font-bold text-ink">Interview</span> stage to schedule a meeting.
                                        </p>
                                    )}
                                    {(c.interviews || []).length > 0 && (
                                        <div className="space-y-2">
                                            {c.interviews.map((iv) => (
                                                <div
                                                    key={iv.event_id}
                                                    data-testid={`interview-row-${iv.event_id}`}
                                                    className="border border-[#E5E5E5] p-3 text-sm"
                                                >
                                                    <div className="flex items-start justify-between gap-2">
                                                        <p className="font-bold text-ink">{iv.summary}</p>
                                                        {iv.interview_type && (
                                                            <span
                                                                className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 border border-[#E5E5E5] text-[#525252] whitespace-nowrap"
                                                                data-testid={`interview-type-badge-${iv.event_id}`}
                                                            >
                                                                {iv.interview_type === "face_to_face"
                                                                    ? "In-person"
                                                                    : iv.interview_type === "telephonic"
                                                                      ? "Phone"
                                                                      : "Virtual"}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-[#525252] mt-1">
                                                        {new Date(iv.start).toLocaleString()} · {iv.timezone}
                                                    </p>
                                                    {iv.location && (
                                                        <p className="text-xs text-[#525252] mt-1">📍 {iv.location}</p>
                                                    )}
                                                    {iv.phone_number && (
                                                        <p className="text-xs text-[#525252] mt-1">☎ {iv.phone_number}</p>
                                                    )}
                                                    <div className="flex flex-wrap gap-3 mt-2">
                                                        {iv.meet_link && (
                                                            <a
                                                                href={iv.meet_link}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-xs font-bold text-cobalt hover:underline inline-flex items-center gap-1"
                                                            >
                                                                <VideoCamera size={12} weight="bold" /> Join Meet
                                                            </a>
                                                        )}
                                                        {iv.html_link && (
                                                            <a
                                                                href={iv.html_link}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-xs font-bold text-[#525252] hover:text-ink inline-flex items-center gap-1"
                                                            >
                                                                <ArrowSquareOut size={12} weight="bold" /> Open in Calendar
                                                            </a>
                                                        )}
                                                    </div>
                                                    {/* V1.5.6.4 — Attended toggle. Only becomes actionable
                                                        once the interview end time has passed; before that
                                                        we still render as "Pending" for visibility. */}
                                                    <InterviewAttendanceToggle
                                                        candidateId={c.id}
                                                        interview={{ ...iv, id: iv.id || iv.event_id }}
                                                        onUpdated={fetchCandidate}
                                                    />
                                                    <InterviewAssessmentPanel
                                                        candidateId={c.id}
                                                        interview={{ ...iv, id: iv.id || iv.event_id }}
                                                        onUpdated={fetchCandidate}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {showSchedule && c && (
                                <ScheduleInterviewModal
                                    candidate={c}
                                    job={job}
                                    onClose={() => setShowSchedule(false)}
                                    onScheduled={() => {
                                        fetchCandidate();
                                        onUpdated?.();
                                    }}
                                />
                            )}

                            {showEmail && c && (
                                <EmailCandidateModal
                                    candidate={c}
                                    jobTitle={job?.title || ""}
                                    clientName={job?.client_name || job?.client?.name || ""}
                                    recruiterName={me?.name || ""}
                                    onClose={() => setShowEmail(false)}
                                    onSent={() => {
                                        fetchCandidate();
                                        onUpdated?.();
                                    }}
                                />
                            )}

                            {showWhatsApp && c && (
                                <WhatsAppCandidateModal
                                    candidate={c}
                                    jobTitle={job?.title || ""}
                                    recruiterName={me?.name || ""}
                                    onClose={() => setShowWhatsApp(false)}
                                    onSent={() => {
                                        fetchCandidate();
                                        onUpdated?.();
                                    }}
                                />
                            )}

                            {outcomeModal && outcomeModal !== "interview" && c && (
                                <OfferHireOutcomeModal
                                    kind={outcomeModal}
                                    candidateId={c.id}
                                    existing={outcomeModal === "offer" ? c.offer_outcome : c.hire_outcome}
                                    stageHistory={c.stage_history}
                                    onClose={() => setOutcomeModal(null)}
                                    onSaved={() => {
                                        setOutcomeModal(null);
                                        fetchCandidate();
                                        onUpdated?.();
                                    }}
                                />
                            )}

                            {outcomeModal === "interview" && c && (
                                <InterviewOutcomeModal
                                    candidateId={c.id}
                                    candidate={c}
                                    onClose={() => setOutcomeModal(null)}
                                    onSaved={() => {
                                        setOutcomeModal(null);
                                        fetchCandidate();
                                        onUpdated?.();
                                    }}
                                />
                            )}

                            {showBookingModal && c && (
                                <SendBookingLinkModal
                                    candidateId={c.id}
                                    candidate={c}
                                    onClose={() => setShowBookingModal(false)}
                                />
                            )}

                            <EditCandidateModal
                                open={showEditModal}
                                candidate={c}
                                onClose={() => setShowEditModal(false)}
                                onSaved={(updated) => {
                                    setC(updated);
                                    onUpdated?.();
                                }}
                            />

                            {/* Email history */}
                            {canWrite && (c.emails || []).length > 0 && (
                                <div data-testid="email-history">
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                                        Email outreach
                                    </p>
                                    <div className="space-y-2">
                                        {(c.emails || []).map((m) => (
                                            <div
                                                key={m.message_id}
                                                className="border border-[#E5E5E5] p-3 text-sm"
                                            >
                                                <p className="font-bold text-ink">{m.subject}</p>
                                                <p className="text-xs text-[#525252] mt-1">
                                                    Sent {new Date(m.sent_at).toLocaleString()} · from {m.from_email}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Skills */}
                            {c.skills?.length > 0 && (
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                                        Skills
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {c.skills.map((s, i) => (
                                            <span
                                                key={`skill-${i}-${s}`}
                                                className="border border-[#E5E5E5] px-2.5 py-1 text-xs font-bold"
                                            >
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Similar candidates by semantic embeddings + skill overlap overlay */}
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                                    Similar candidates
                                </p>
                                <SimilarCandidates
                                    candidateId={c.id}
                                    onOpenCandidate={(otherId) => {
                                        if (onOpenCandidate) onOpenCandidate(otherId);
                                    }}
                                    limit={6}
                                />
                            </div>

                            {/* Resume */}
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                                    Resume
                                </p>
                                {canWrite && (
                                    <ResumeActions
                                        candidate={c}
                                        onReparsed={fetchCandidate}
                                    />
                                )}
                                <div className="border border-[#E5E5E5] bg-[#F9F9F9] p-4 text-sm whitespace-pre-wrap text-[#525252] max-h-72 overflow-y-auto">
                                    {c.resume_text}
                                </div>
                            </div>

                            {/* References — V1.3, Feb 2026 */}
                            <div>
                                <ReferencesSection candidateId={c.id} />
                            </div>

                            {/* Notes — hidden on profile-only (foreign) rows */}
                            {!c._pipeline_redacted && (
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                                    Interview Notes
                                </p>
                                <div className="space-y-2 mb-3">
                                    {(c.notes || []).map((n) => (
                                        <div
                                            key={n.id}
                                            className="border border-[#E5E5E5] p-3 text-sm"
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <p className="font-bold text-ink">{n.author}</p>
                                                <div className="flex items-center gap-0.5 text-[#F59E0B]">
                                                    {[...Array(n.rating || 0)].map((_, i) => (
                                                        <Star key={`${n.id}-star-${i}`} size={12} weight="fill" />
                                                    ))}
                                                </div>
                                            </div>
                                            <p className="text-[#525252]">{n.text}</p>
                                        </div>
                                    ))}
                                    {(!c.notes || c.notes.length === 0) && (
                                        <p className="text-sm text-[#A3A3A3]">No notes yet.</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <textarea
                                        data-testid="note-input"
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        rows={3}
                                        placeholder="Add interview feedback…"
                                        className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                                    />
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1">
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <button
                                                    key={n}
                                                    onClick={() => setRating(n)}
                                                    data-testid={`rating-${n}`}
                                                    className={n <= rating ? "text-[#F59E0B]" : "text-[#E5E5E5]"}
                                                >
                                                    <Star size={18} weight="fill" />
                                                </button>
                                            ))}
                                        </div>
                                        <button
                                            data-testid="add-note-button"
                                            onClick={addNote}
                                            className="bg-cobalt hover:bg-cobalt-dark text-white px-4 py-2 text-xs font-bold uppercase tracking-wider"
                                        >
                                            Add note
                                        </button>
                                    </div>
                                </div>
                            </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ---------- Publish to Marketplace toggle ----------
function PublishToMarketplaceToggle({ candidate, onChanged }) {
    const [busy, setBusy] = React.useState(false);
    const published = !!candidate.marketplace_published;

    const toggle = async () => {
        if (published) {
            // Un-publish is a simple flip, no consent needed.
            if (!window.confirm("Remove this candidate from the cross-workspace Marketplace?")) return;
            setBusy(true);
            try {
                await api.post(`/candidates/${candidate.id}/publish-to-marketplace`,
                    { publish: false, consent_confirmed: false });
                toast.success("Removed from Marketplace.");
                onChanged?.();
            } catch (err) {
                toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Failed");
            } finally { setBusy(false); }
            return;
        }
        // Consent gate before publishing.
        const confirmed = window.confirm(
            `Publish ${candidate.name || "this candidate"} to the cross-workspace Talent Marketplace?\n\n` +
            `By clicking OK you confirm the candidate has given consent for their profile ` +
            `to be visible to recruiters at other agencies on NaukriDaddy. This action is audited.`
        );
        if (!confirmed) return;
        setBusy(true);
        try {
            await api.post(`/candidates/${candidate.id}/publish-to-marketplace`,
                { publish: true, consent_confirmed: true });
            toast.success("Published to Marketplace.");
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="publish-marketplace-toggle"
             className={`p-4 border ${published ? "border-splash bg-splash/10" : "border-[#E5E5E5] bg-[#FAFAFA]"}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">
                        Cross-workspace exposure
                    </p>
                    <p className="font-heading text-sm font-black text-ink">
                        {published ? "Visible in NaukriDaddy Marketplace" : "Private to your workspace"}
                    </p>
                    <p className="text-[11px] text-[#525252] mt-1 max-w-md">
                        {published
                            ? "Recruiters at other agencies can find this candidate via the Talent Marketplace. Contact reveals go through DPDPA audit."
                            : "Only your workspace sees this profile. Enable publish to let this strong candidate be discovered by more recruiters."}
                    </p>
                </div>
                <button
                    onClick={toggle}
                    disabled={busy}
                    data-testid="publish-marketplace-btn"
                    className={`text-[10px] font-black uppercase tracking-wider px-4 py-2 border transition-colors disabled:opacity-60 ${
                        published
                            ? "bg-white border-[#E5E5E5] text-[#525252] hover:border-ink"
                            : "bg-ink text-white border-ink hover:bg-cobalt"
                    }`}
                >
                    {busy ? "…" : (published ? "Unpublish" : "Publish · consent given")}
                </button>
            </div>
        </div>
    );
}

